// @story: libs/storybook/stories/modules/licensing/active-licences-dialog.stories.ts
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon, UserPreferencesPanel } from '@zag/ui';
import type { ProfileUser } from '@zag/ui';
import { ACTIVE_LICENCES_DIALOG_CONFIG, ActiveLicencesDialogComponent, FeatureAccessClientService } from '@zag/features/ui';

interface ProfileDialogResult {
  action: 'signOut' | 'navigateToProfile';
}

/**
 * Profile dialog component for the control UI.
 * Displays user profile with language switcher, theme toggle, and sign out.
 */
@Component({
  selector: 'ctrl-profile-dialog',
  imports: [TranslatePipe, ZagIcon, UserPreferencesPanel],
  template: `
    <div class="profile-dialog" role="dialog" aria-labelledby="profile-title">
      <!-- Header -->
      <div class="profile-dialog__header">
        <h2 id="profile-title" class="profile-dialog__title">{{ 'navigation.userMenu' | translate }}</h2>
        <button type="button" class="profile-dialog__close" (click)="onClose()" [attr.aria-label]="'common.close' | translate">
          <span zagIcon="close"></span>
        </button>
      </div>

      <!-- Body with shared panel -->
      <div class="profile-dialog__body">
        <zag-user-preferences-panel
          [user]="user"
          [showHeader]="true"
          profileRoute="/profile"
          [showNotifications]="false"
          (logout)="onSignOut()"
          (linkClick)="onClose()"
          (versionInfoClick)="onVersionInfoClick()"
        />
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .profile-dialog {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: 600px;
        height: auto;
        max-height: 90vh;
        max-height: 90svh;
        background: var(--surface-color-default);
        border-radius: var(--border-radius-lg);
        box-shadow: var(--shadow-xl);
        overflow: hidden;
        animation: slideUp 0.3s ease-out;
      }

      @keyframes slideUp {
        from {
          transform: translateY(20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      /* Header */
      .profile-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-md) var(--spacing-lg);
        border-bottom: 1px solid var(--border-color-default);
        flex-shrink: 0;
      }

      .profile-dialog__title {
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: 600;
        color: var(--text-color-primary);
      }

      .profile-dialog__close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        border-radius: var(--border-radius-md);
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        flex-shrink: 0;
        transition: background-color var(--transition-duration-fast);

        &:hover {
          background-color: var(--surface-color-hover);
        }

        span[zagIcon] {
          font-size: 24px;
        }
      }

      /* Body */
      .profile-dialog__body {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'profile-dialog-host',
  },
})
export class CtrlProfileDialogComponent {
  private readonly dialogRef = inject(DialogRef<ProfileDialogResult>);
  private readonly dialog = inject(Dialog);
  private readonly featureAccess = inject(FeatureAccessClientService);

  protected readonly user: ProfileUser = (this.dialogRef.config.data as { user: ProfileUser })?.user || {
    name: 'User',
  };

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onSignOut(): void {
    this.dialogRef.close({ action: 'signOut' });
  }

  protected onVersionInfoClick(): void {
    this.dialogRef.closed.subscribe(() => {
      this.dialog.open(ActiveLicencesDialogComponent, {
        ...ACTIVE_LICENCES_DIALOG_CONFIG,
        data: { licences: this.featureAccess.licences() ?? [] },
      });
    });
    this.dialogRef.close();
  }
}
